/**
 * useFaults Hook
 *
 * Real-time Firestore listener for the Faults collection
 * Provides filtered views by stage, tech assignment, and monitoring status
 */

import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  QueryConstraint,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { Fault, FaultStage } from '../types/fault.types';
import { FAULT_STAGE_ORDER, migrateFaultStage } from '../types/fault.types';
import useTenantId, { tenantIdReadUnion } from './useTenantId';

/**
 * Convert any Firestore Timestamp values to ISO strings.
 * Prevents React error #310 ("Objects are not valid as a React child")
 * when Timestamp objects are accidentally rendered in JSX.
 */
function toISOString(value: unknown): string {
  if (!value) return '';
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (typeof value === 'string') return value;
  // Numeric epoch (seconds vs milliseconds — 13-digit ≈ ms).
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value < 1e12 ? value * 1000 : value).toISOString();
  }
  if (typeof value === 'object' && value !== null) {
    const v = value as Record<string, unknown>;
    // A Timestamp that survived serialization keeps a callable toDate().
    if (typeof v.toDate === 'function') return (v as unknown as Timestamp).toDate().toISOString();
    // Serialized-Timestamp map. The client SDK writes { seconds, nanoseconds };
    // the Admin SDK / a JSON round-trip writes { _seconds, _nanoseconds }. The
    // old check only matched the un-prefixed pair, so an underscore-prefixed
    // map fell through to String(value) → "[object Object]" → every affected
    // entry was silently dropped by the timeline's `!safeTime(ts)` guard.
    const seconds =
      typeof v.seconds === 'number' ? v.seconds
      : typeof v._seconds === 'number' ? v._seconds
      : undefined;
    if (seconds !== undefined) return new Date(seconds * 1000).toISOString();
  }
  return String(value);
}

/**
 * Sanitize a raw Firestore fault document to ensure all timestamp fields
 * are ISO strings (not Firestore Timestamp objects) and required string
 * fields have safe defaults. This prevents React #310 crashes.
 */
function sanitizeFault(raw: Record<string, unknown>): Fault {
  // Local helper alias for nested timestamp-bearing entries — Firestore data
  // is loosely typed, so we narrow nested objects/arrays as we touch them.
  type TsEntry = Record<string, unknown>;
  // Convert top-level timestamp fields
  const timestampFields = [
    'createdAt', 'updatedAt', 'intakeDate', 'resolvedAt', 'archivedAt',
    'consultRequestedAt', 'consultCompletedAt', 'provisioningSentAt',
    'provisioningCompletedAt', 'sentToBillingAt', 'sentToDealsAt',
    'followUpEmailSentAt', 'followUpCompletedAt', 'deploymentJobCompletedAt',
    'slaResponseDeadline', 'slaResolutionDeadline', 'serviceActivatedAt',
    'billedAt', 'paidAt',
  ];
  for (const field of timestampFields) {
    if (raw[field] && typeof raw[field] !== 'string') {
      raw[field] = toISOString(raw[field]);
    }
  }

  // Ensure required string fields have safe defaults
  if (!raw.clientName || typeof raw.clientName !== 'string') raw.clientName = raw.clientName ? String(raw.clientName) : 'Unknown';
  if (!raw.symptom || typeof raw.symptom !== 'string') raw.symptom = raw.symptom ? String(raw.symptom) : '';
  if (!raw.address || typeof raw.address !== 'string') raw.address = raw.address ? String(raw.address) : '';
  if (!raw.intakeNotes || typeof raw.intakeNotes !== 'string') raw.intakeNotes = raw.intakeNotes ? String(raw.intakeNotes) : '';

  // Ensure arrays have safe defaults
  if (!Array.isArray(raw.remoteLogs)) raw.remoteLogs = [];
  if (!Array.isArray(raw.callNotes)) raw.callNotes = [];
  if (!Array.isArray(raw.clientUpdates)) raw.clientUpdates = [];
  if (!Array.isArray(raw.investigationHistory)) raw.investigationHistory = [];

  // Sanitize nested timestamp arrays
  for (const entry of raw.investigationHistory as TsEntry[]) {
    if (entry.timestamp && typeof entry.timestamp !== 'string') {
      entry.timestamp = toISOString(entry.timestamp);
    }
  }
  for (const entry of raw.callNotes as TsEntry[]) {
    if (entry.timestamp && typeof entry.timestamp !== 'string') {
      entry.timestamp = toISOString(entry.timestamp);
    }
  }
  for (const entry of raw.remoteLogs as TsEntry[]) {
    if (entry.timestamp && typeof entry.timestamp !== 'string') {
      entry.timestamp = toISOString(entry.timestamp);
    }
  }
  for (const entry of raw.clientUpdates as TsEntry[]) {
    if (entry.timestamp && typeof entry.timestamp !== 'string') {
      entry.timestamp = toISOString(entry.timestamp);
    }
    if (entry.nextUpdateDue && typeof entry.nextUpdateDue !== 'string') {
      entry.nextUpdateDue = toISOString(entry.nextUpdateDue);
    }
  }

  // Sanitize lastConsultResponse timestamps
  if (raw.lastConsultResponse) {
    const lcr = raw.lastConsultResponse as TsEntry;
    if (lcr.completedAt && typeof lcr.completedAt !== 'string') {
      lcr.completedAt = toISOString(lcr.completedAt);
    }
  }

  // Sanitize deploymentJobSummary nested timestamps
  const deploymentJobSummary = raw.deploymentJobSummary as
    | { fieldLogEntries?: TsEntry[] }
    | undefined;
  if (deploymentJobSummary?.fieldLogEntries) {
    for (const entry of deploymentJobSummary.fieldLogEntries) {
      if (entry.timestamp && typeof entry.timestamp !== 'string') {
        entry.timestamp = toISOString(entry.timestamp);
      }
    }
  }

  // Sanitize monitoringPhase timestamps
  if (raw.monitoringPhase) {
    const monitoringPhase = raw.monitoringPhase as TsEntry;
    if (monitoringPhase.startedAt && typeof monitoringPhase.startedAt !== 'string') {
      monitoringPhase.startedAt = toISOString(monitoringPhase.startedAt);
    }
    if (monitoringPhase.expiresAt && typeof monitoringPhase.expiresAt !== 'string') {
      monitoringPhase.expiresAt = toISOString(monitoringPhase.expiresAt);
    }
  }

  // Sanitize relatedFaults timestamps
  if (Array.isArray(raw.relatedFaults)) {
    for (const rf of raw.relatedFaults as TsEntry[]) {
      if (rf.createdAt && typeof rf.createdAt !== 'string') {
        rf.createdAt = toISOString(rf.createdAt);
      }
    }
  }

  return raw as unknown as Fault;
}

/**
 * Live subscription to ONE fault document.
 *
 * Deliberately a single-doc `onSnapshot`, not a list query: per CLAUDE.md the
 * tenant rules reject a *list* read that carries no `tenantId` constraint, but
 * single-doc reads are unaffected (`belongsToCurrentTenant()` still gates the
 * document itself, so a cross-tenant id resolves to null rather than leaking).
 *
 * Exists because the tech job detail had NO live view of the fault at all — it
 * read `job.briefingHistory`, a snapshot frozen at job-creation time. Every
 * office note added to the fault after the job was raised (the common case:
 * the office keeps investigating after booking the visit) was invisible in the
 * field. See `useJobFault` for the job-shaped wrapper.
 */
export function useFault(faultId: string | null | undefined): {
  fault: Fault | null;
  loading: boolean;
} {
  const [fault, setFault] = useState<Fault | null>(null);
  const [loading, setLoading] = useState(!!faultId);

  useEffect(() => {
    if (!faultId) {
      setFault(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, 'faults', faultId),
      (snap) => {
        setFault(
          snap.exists()
            ? sanitizeFault({ id: snap.id, ...snap.data() } as Record<string, unknown>)
            : null,
        );
        setLoading(false);
      },
      (err) => {
        // A deleted fault or a permissions blip must never break the job
        // screen — the frozen briefingHistory remains as the fallback.
        console.warn('[useFault] subscription error:', err);
        setFault(null);
        setLoading(false);
      },
    );
    return unsub;
  }, [faultId]);

  return { fault, loading };
}

/**
 * Main hook: Get all faults with optional filtering
 */
export function useFaults(options?: {
  includeArchived?: boolean;
  stage?: FaultStage;
  assignedTo?: string;
  assignedTechId?: string;
}) {
  const tenantId = useTenantId();
  const [faults, setFaults] = useState<Fault[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const constraints: QueryConstraint[] = [
      // Required for strict tenant-isolation rules — see useDeals comment
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    ];

    // Filter archived
    if (!options?.includeArchived) {
      constraints.push(where('archived', '==', false));
    }

    // Filter by stage
    if (options?.stage) {
      constraints.push(where('stage', '==', options.stage));
    }

    // Filter by assigned office staff
    if (options?.assignedTo) {
      constraints.push(where('assignedTo', '==', options.assignedTo));
    }

    // Filter by assigned tech
    if (options?.assignedTechId) {
      constraints.push(where('assignedTechId', '==', options.assignedTechId));
    }

    // NOTE: no server-side `orderBy('createdAt')` — it silently excludes faults
    // missing the field. Sort client-side (newest first) after sanitisation
    // instead, so field-absent docs still surface in global search.

    const q = query(collection(db, 'faults'), ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const faultData = snapshot.docs
          .map((doc) => sanitizeFault({ id: doc.id, ...doc.data() }))
          .sort((a, b) =>
            String(b.createdAt || '').localeCompare(String(a.createdAt || '')),
          );

        setFaults(faultData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching faults:', err);
        setError(err instanceof Error ? err : new Error(String((err as Error)?.message || err)));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [
    tenantId,
    options?.includeArchived,
    options?.stage,
    options?.assignedTo,
    options?.assignedTechId,
  ]);

  return { faults, loading, error };
}

// NOTE: useFaultsByStage / useTechConsultRequests (the fault-stage-query
// variants) were removed in the Jul 2026 stage redesign — both were dead code.
// The live consult path is hooks/useFaultConsultRequests.ts, which queries the
// fault_consult_requests collection by status (stage-free). Stage-value
// Firestore queries can't see legacy stage strings; filter client-side through
// migrateFaultStage instead.

/**
 * Get all faults flagged for billing (sentToBilling: true).
 * Includes archived faults — fault may be archived post-resolution but still needs invoicing.
 * Used by /home/billing to show fault callouts alongside deals.
 */
export function useFaultsForBilling() {
  const tenantId = useTenantId();
  const [faults, setFaults] = useState<Fault[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Cap at 500 faults flagged for billing. The billing page renders the
    // backlog; in normal operations this set stays under a couple dozen.
    // The cap is a cost ceiling so a stuck-billing scenario can't bleed
    // reads forever. Sort stays client-side intentionally — adding a
    // server-side orderBy on sentToBillingAt would (a) require a new
    // composite index whose deploy could lag the app deploy and break
    // the billing page, and (b) silently drop any historical fault docs
    // missing the sentToBillingAt field (Firestore orderBy excludes
    // docs where the ordered field is absent).
    const q = query(
      collection(db, 'faults'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('sentToBilling', '==', true),
      limit(500)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs
          .map((doc) => sanitizeFault({ id: doc.id, ...doc.data() }))
          .sort((a: Fault, b: Fault) => (b.sentToBillingAt ?? '').localeCompare(a.sentToBillingAt ?? ''));
        setFaults(data);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching faults for billing:', err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tenantId]);

  return { faults, loading };
}

/**
 * Get faults in active monitoring phase
 */
export function useMonitoringFaults() {
  const tenantId = useTenantId();
  const [monitoringFaults, setMonitoringFaults] = useState<Fault[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, 'faults'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('monitoringPhase.active', '==', true),
      orderBy('monitoringPhase.startedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const faults = snapshot.docs.map((doc) =>
        sanitizeFault({ id: doc.id, ...doc.data() })
      );

      // Filter out expired monitoring periods
      const now = new Date();
      const activeFaults = faults.filter((fault: Fault) => {
        if (!fault.monitoringPhase) return false;
        const expiresAt = new Date(fault.monitoringPhase.expiresAt);
        return now < expiresAt;
      });

      setMonitoringFaults(activeFaults);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [tenantId]);

  return { monitoringFaults, loading };
}

/**
 * Get fault statistics for dashboard
 */
export function useFaultStats() {
  const { faults, loading } = useFaults();
  const { faults: archivedFaults } = useFaults({ includeArchived: true });

  const stats = useMemo(() => {
    const totalOpen = faults.length;

    // Seeded from FAULT_STAGE_ORDER so the record stays in lock-step with
    // the stage enum.
    const byStage = Object.fromEntries(
      FAULT_STAGE_ORDER.map((s) => [s, 0])
    ) as Record<FaultStage, number>;
    faults.forEach((f: Fault) => {
      const migrated = migrateFaultStage(f.stage);
      byStage[migrated]++;
    });

    const bySeverity = {
      low: faults.filter((f: Fault) => f.severity === 'low').length,
      medium: faults.filter((f: Fault) => f.severity === 'medium').length,
      high: faults.filter((f: Fault) => f.severity === 'high').length,
      critical: faults.filter((f: Fault) => f.severity === 'critical').length,
    };

    // Calculate average resolution time (for completed faults)
    const completedFaults = faults.filter((f: Fault) => f.resolvedAt);
    const totalResolutionTime = completedFaults.reduce((acc: number, fault: Fault) => {
      const start = new Date(fault.createdAt).getTime();
      const end = new Date(fault.resolvedAt!).getTime();
      return acc + (end - start);
    }, 0);

    const averageResolutionTime =
      completedFaults.length > 0
        ? totalResolutionTime / completedFaults.length / (1000 * 60 * 60) // Convert to hours
        : 0;

    // Calculate deployment rate (faults that became deployment jobs)
    const resolvedFaults = archivedFaults.filter((f: Fault) => f.resolvedAt);
    const deployedFaults = resolvedFaults.filter((f: Fault) => f.resolutionType === 'deployed');
    const deploymentRate =
      resolvedFaults.length > 0
        ? (deployedFaults.length / resolvedFaults.length) * 100
        : 0;

    // Calculate remote resolution rate (resolved without deployment)
    const remoteResolvedFaults = resolvedFaults.filter((f: Fault) => f.resolutionType === 'resolved_remotely');
    const remoteResolutionRate =
      resolvedFaults.length > 0
        ? (remoteResolvedFaults.length / resolvedFaults.length) * 100
        : 0;

    // Calculate SLA compliance (percentage of resolved faults with SLA met)
    const resolvedWithSla = archivedFaults.filter(
      (f: Fault) => f.resolvedAt && f.slaResolutionDeadline
    );
    const slaMet = resolvedWithSla.filter(
      (f: Fault) => f.slaResolutionMet === true && f.slaResponseMet !== false
    );
    const slaCompliance =
      resolvedWithSla.length > 0
        ? (slaMet.length / resolvedWithSla.length) * 100
        : 100;

    return {
      totalOpen,
      byStage,
      bySeverity,
      averageResolutionTime,
      deploymentRate,
      remoteResolutionRate,
      slaCompliance,
    };
  }, [faults, archivedFaults]);

  return { stats, loading };
}
